// src/pages/loadletter/LetterGeneration.tsx

import React, { useState, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import {
  FileText,
  Send,
  Download,
  Eye,
  CheckCircle,
  Users,
  Search,
  AlertTriangle,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { useGrid } from '../../contexts/GridContext';
import { useAcademicYear } from '../../contexts/AcademicYearContext';
import { YearSelector } from '../../components/common/YearSelector';
import {
  getLoadLetters,
  upsertLoadLetterIfChanged,
  releaseLoadLetters,
  releaseLoadLetter,
  markLettersAsReady,
  markLetterAsReady,
  deleteLoadLetter,
  deleteLoadLetters,
  getActiveTemplates,
  getLoadLetterSettings,
} from '../../services/loadLetterService';
import { batchGenerateLoadLetters } from '../../utils/loadLetterGenerator';
import { predominantSemester, otherSemesterLetters, semesterLabel } from '../../utils/letterSemesters';
import {
  LoadLetter,
  LoadLetterTemplate,
  LoadLetterSettings,
  Semester,
  LoadLetterFilters,
} from '../../types/loadLetter';
import { getLinesForSemester, getGridStats, validateGridForLoadLetters } from '../../services/gridLoadLetterService';
import { generateLoadLetterPdf } from '../../utils/loadLetterPdfGenerator';
import { DEFAULT_MINUTES_PER_LINE } from '../../types/schoolConfig';
import { displayLearningArea } from '../../constants/learningAreas';
import EmptyState from '../../components/ui/EmptyState';
import Spinner from '../../components/ui/Spinner';

const LetterGeneration: React.FC = () => {
  const { addToast } = useToast();
  const confirm = useConfirm();
  const { staff } = useData();
  const { user } = useAuth();
  const { grids: allGrids, activeGrid: selectedGrid, loading: gridLoading, error: gridError, setActiveGrid } = useGrid();

  // Academic year filtering
  let workingYear: number;
  try {
    const academicYearContext = useAcademicYear();
    workingYear = academicYearContext.workingYear;
  } catch (e) {
    workingYear = new Date().getFullYear();
  }

  // Filter grids by working year
  const grids = allGrids.filter(g => g.year === workingYear);

  // Default the semester selector to the current academic period, looking ahead
  // to the period being planned (pairs with AcademicYearContext, which rolls the
  // working year forward in July):
  //   - A future working year (planning ahead) starts at Semester 1.
  //   - Current working year: Jan–Apr -> Semester 1; May onwards -> Semester 2.
  const getDefaultSemester = (): Semester => {
    const now = new Date();
    if (workingYear > now.getFullYear()) return 'semester1';
    return now.getMonth() >= 4 ? 'semester2' : 'semester1';
  };

  // Helper functions
  const selectGrid = (gridId: string) => {
    setActiveGrid(gridId);
  };

  const getValidationStatus = () => {
    return validateGridForLoadLetters(selectedGrid);
  };

  const getStats = () => {
    if (!selectedGrid) return null;
    return getGridStats(selectedGrid);
  };

  const getLines = (semester: Semester) => {
    if (!selectedGrid) return [];
    return getLinesForSemester(selectedGrid, semester);
  };

  // States
  const [loadLetters, setLoadLetters] = useState<LoadLetter[]>([]);
  const [templates, setTemplates] = useState<LoadLetterTemplate[]>([]);
  const [settings, setSettings] = useState<LoadLetterSettings | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [selectedSemester, setSelectedSemester] = useState<Semester>(getDefaultSemester);
  // Once someone picks a semester, stop steering it to the existing letters' one.
  const semesterPicked = useRef(false);
  const [selectedLetters, setSelectedLetters] = useState<string[]>([]);
  const [selectedLetter, setSelectedLetter] = useState<LoadLetter | null>(null);
  const [selectedGridId, setSelectedGridId] = useState<string>('all'); // 'all' or specific grid ID

  // UI States
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [showReleaseModal, setShowReleaseModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isReleasing, setIsReleasing] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [generationProgress, setGenerationProgress] = useState({ current: 0, total: 0 });

  // Filters
  const [filterDepartment, setFilterDepartment] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Fetch initial data
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch templates
      const templatesData = await getActiveTemplates();
      setTemplates(templatesData);

      // Set default template
      const defaultTemplate = templatesData.find(t => t.isDefault);
      if (defaultTemplate) {
        setSelectedTemplate(defaultTemplate.id);
      }

      // Fetch settings
      const settingsData = await getLoadLetterSettings();
      setSettings(settingsData);

      // Fetch existing letters
      await fetchLetters();
    } catch (error) {
      console.error('Error fetching data:', error);
      addToast('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchLetters = async () => {
    try {
      const filters: LoadLetterFilters = {};
      filters.academicYear = String(workingYear);

      const letters = await getLoadLetters(filters);
      setLoadLetters(letters);

      // Letters are matched by staff + year + semester, so start on the semester
      // this year's letters already use; otherwise regenerating with the default
      // makes a second set instead of updating them (#286).
      if (!semesterPicked.current) {
        const existing = predominantSemester(letters);
        if (existing) setSelectedSemester(existing);
      }
    } catch (error) {
      console.error('Error fetching letters:', error);
      addToast('Failed to load letters', 'error');
    }
  };

  // Generate load letters for selected staff or all staff
  const handleGenerateLetters = async (staffIds?: string[]) => {
    if (!user || !settings) {
      addToast('User or settings not loaded', 'error');
      return;
    }

    const otherSemesters = otherSemesterLetters(loadLetters, selectedSemester);
    if (otherSemesters.length > 0) {
      const selectedLabel = semesterLabel(selectedSemester);
      const existing = otherSemesters.map(s => `${s.count} ${semesterLabel(s.semester)}`).join(' and ');
      const proceed = await confirm({
        title: `Create ${selectedLabel} letters?`,
        message: `${workingYear} already has ${existing} letters. ` +
          `Generating ${selectedLabel} letters creates a separate letter for each staff member and does not update the existing ones. ` +
          `To update the existing letters instead, cancel and set Semester to ${semesterLabel(otherSemesters[0].semester)}.`,
        confirmLabel: `Create ${selectedLabel} letters`,
      });
      if (!proceed) return;
    }

    setIsGenerating(true);
    setGenerationProgress({ current: 0, total: 0 });

    try {
      // Get staff to generate letters for
      // Note: Generate for ALL staff, regardless of status (active/inactive only controls login access)
      const requestedStaff = staffIds
        ? staff.filter(s => staffIds.includes(s.id))
        : staff;

      // Staff on 0 FTE (e.g. on leave) have no teaching load to report (#288).
      const zeroFteStaff = requestedStaff.filter(s => s.fte === 0);
      const targetStaff = requestedStaff.filter(s => s.fte !== 0);
      if (zeroFteStaff.length > 0) {
        addToast(
          `Skipped ${zeroFteStaff.length} staff on 0 FTE: ${zeroFteStaff.map(s => s.name).join(', ')}`,
          'info'
        );
      }

      if (targetStaff.length === 0) {
        addToast('No staff members to generate letters for', 'warning');
        return;
      }

      // Validate grid selection
      if (selectedGridId !== 'all' && !selectedGrid) {
        addToast('Please select a grid first', 'error');
        return;
      }

      if (selectedGridId === 'all' && grids.length === 0) {
        addToast('No grids available', 'error');
        return;
      }

      // Validate individual grid if selected
      if (selectedGridId !== 'all') {
        const validation = getValidationStatus();
        if (!validation.valid) {
          addToast(`Grid validation failed: ${validation.errors.join(', ')}`, 'error');
          return;
        }
      }

      addToast(`Generating ${targetStaff.length} load letters...`, 'info');

      // Get grid and lines data for the selected semester
      let grid: any;
      let lines: any[];

      if (selectedGridId === 'all') {
        // Combine all grids - aggregate classes from all grids
        const allClasses: any[] = [];
        const allLines: any[] = [];
        let defaultMinutes = 440;
        const gridNames: string[] = [];

        grids.forEach(g => {
          if (g.name) {
            gridNames.push(g.name);
          }
          if (g.classes) {
            // Tag each class with its source grid name AND default minutes for RUAH detection and correct minute calculation
            const taggedClasses = g.classes.map(c => ({
              ...c,
              sourceGridName: g.name,
              sourceGridDefaultMinutes: g.defaultClassMinutes || DEFAULT_MINUTES_PER_LINE
            }));
            allClasses.push(...taggedClasses);
          }
          if (g.defaultClassMinutes) {
            defaultMinutes = g.defaultClassMinutes; // Use the last one found
          }

          // Get lines for this grid - ALL semesters (extraction will filter, but we want to show all)
          const semester1Lines = getLinesForSemester(g, 'semester1');
          const semester2Lines = getLinesForSemester(g, 'semester2');
          const gridLines = [...semester1Lines, ...semester2Lines];

          gridLines.forEach(line => {
            // Avoid duplicate line IDs
            if (!allLines.find(l => l.id === line.id)) {
              allLines.push(line);
            }
          });
        });

        grid = {
          classes: allClasses,
          defaultClassMinutes: defaultMinutes,
          name: gridNames.join(' + '), // Preserve grid names for RUAH detection
        };

        lines = allLines.map(line => ({
          id: line.id,
          name: line.name,              // Preserve name for RUAH detection
          studyPeriod: line.studyPeriod, // Preserve studyPeriod for RUAH detection
          semester: line.studyPeriod === 'fullyear' ? 'semester1' : line.studyPeriod,
          minutesPerFortnight: defaultMinutes,
        }));
      } else {
        // Single grid - tag classes with source grid name AND default minutes
        const taggedClasses = (selectedGrid!.classes || []).map(c => ({
          ...c,
          sourceGridName: selectedGrid!.name,
          sourceGridDefaultMinutes: selectedGrid!.defaultClassMinutes || DEFAULT_MINUTES_PER_LINE
        }));

        grid = {
          classes: taggedClasses,
          defaultClassMinutes: selectedGrid!.defaultClassMinutes,
          name: selectedGrid!.name, // Preserve grid name for RUAH detection
        };

        // Get ALL semester lines (extraction will filter, but we want to show all classes)
        const allSemesterLines = [
          ...getLines('semester1'),
          ...getLines('semester2')
        ];

        lines = allSemesterLines.map(line => ({
          id: line.id,
          name: line.name,              // Preserve name for RUAH detection
          studyPeriod: line.studyPeriod, // Preserve studyPeriod for RUAH detection
          semester: line.studyPeriod === 'fullyear' ? 'semester1' : line.studyPeriod,
          minutesPerFortnight: selectedGrid!.defaultClassMinutes || DEFAULT_MINUTES_PER_LINE,
        }));
      }

      // Generate letters
      const result = await batchGenerateLoadLetters(
        targetStaff as any,
        grid,
        lines,
        {
          academicYear: String(workingYear),
          semester: selectedSemester,
          userId: user.uid || user.id,
        },
        (current, total) => {
          setGenerationProgress({ current, total });
        }
      );

      const { letters: generatedLetters, errors } = result;

      // Log errors for debugging
      if (errors.length > 0) {
        console.error('❌ Failed to generate letters for the following staff:', errors);
      }

      // Save letters to database via content-aware upsert:
      // - new staff get a v1 letter
      // - unchanged letters are left alone
      // - changed letters supersede the previous version (staff re-acknowledge)
      let createdCount = 0;
      let updatedCount = 0;
      let unchangedCount = 0;
      let saveErrorCount = 0;
      for (const letter of generatedLetters) {
        try {
          const { action } = await upsertLoadLetterIfChanged(letter);
          if (action === 'created') createdCount++;
          else if (action === 'updated') updatedCount++;
          else unchangedCount++;
        } catch (error) {
          saveErrorCount++;
          console.error('Error saving letter:', error);
        }
      }

      const totalErrors = errors.length + saveErrorCount;
      const summary = `Created ${createdCount} · Updated ${updatedCount} · Unchanged ${unchangedCount}`;

      if (totalErrors > 0) {
        addToast(
          `${summary}. ${totalErrors} staff had errors (check console for details).`,
          'warning'
        );
      } else if (createdCount === 0 && updatedCount === 0) {
        addToast(`All letters already up to date (${unchangedCount} unchanged)`, 'info');
      } else {
        addToast(summary, 'success');
      }

      await fetchLetters(); // Refresh the list
    } catch (error) {
      console.error('Error generating letters:', error);
      addToast('Failed to generate load letters', 'error');
    } finally {
      setIsGenerating(false);
      setGenerationProgress({ current: 0, total: 0 });
    }
  };

  // Mark selected letters as ready (bulk)
  const handleMarkAsReady = async () => {
    if (selectedLetters.length === 0) {
      addToast('Please select at least one letter to mark as ready', 'warning');
      return;
    }

    // Filter to only letters that are in draft status
    const draftLetters = loadLetters.filter(
      l => selectedLetters.includes(l.id) && l.status === 'draft'
    );

    if (draftLetters.length === 0) {
      addToast('No draft letters selected', 'warning');
      return;
    }

    const draftIds = draftLetters.map(l => l.id);

    try {
      await markLettersAsReady(draftIds);
      addToast(`Marked ${draftIds.length} letter(s) as ready for release`, 'success');
      await fetchLetters();
    } catch (error) {
      console.error('Error marking letters as ready:', error);
      addToast('Failed to mark letters as ready', 'error');
    }
  };

  // Mark a single letter as ready
  const handleMarkSingleReady = async (letterId: string) => {
    try {
      await markLetterAsReady(letterId);
      addToast('Letter marked as ready for release', 'success');
      await fetchLetters();
    } catch (error) {
      console.error('Error marking letter as ready:', error);
      addToast('Failed to mark letter as ready', 'error');
    }
  };

  // Release selected letters
  const handleReleaseLetters = async () => {
    if (!user || !settings) return;

    if (selectedLetters.length === 0) {
      addToast('Please select at least one letter to release', 'warning');
      return;
    }

    // Filter to only letters that are ready for release
    const readyLetters = loadLetters.filter(
      l => selectedLetters.includes(l.id) && l.status === 'ready'
    );

    if (readyLetters.length === 0) {
      addToast('No ready letters selected. Please mark letters as ready first.', 'warning');
      return;
    }

    const readyIds = readyLetters.map(l => l.id);

    setIsReleasing(true);
    try {
      await releaseLoadLetters(readyIds, {
        semester: selectedSemester,
        academicYear: String(workingYear),
        createdBy: user.uid || user.id,
      });

      addToast(`Successfully released ${readyIds.length} letters to staff`, 'success');
      setSelectedLetters([]);
      setShowReleaseModal(false);
      await fetchLetters();
    } catch (error) {
      console.error('Error releasing letters:', error);
      addToast('Failed to release letters', 'error');
    } finally {
      setIsReleasing(false);
    }
  };

  // Release a single letter
  const handleReleaseSingle = async (letterId: string) => {
    try {
      await releaseLoadLetter(letterId);
      addToast('Letter released to staff', 'success');
      await fetchLetters();
    } catch (error) {
      console.error('Error releasing letter:', error);
      addToast('Failed to release letter', 'error');
    }
  };

  // Delete selected letters (bulk)
  const handleDeleteLetters = async () => {
    if (selectedLetters.length === 0) {
      addToast('Please select at least one letter to delete', 'warning');
      return;
    }

    setIsDeleting(true);
    try {
      await deleteLoadLetters(selectedLetters);
      addToast(`Successfully deleted ${selectedLetters.length} letter(s)`, 'success');
      setSelectedLetters([]);
      setShowDeleteModal(false);
      await fetchLetters();
    } catch (error) {
      console.error('Error deleting letters:', error);
      addToast('Failed to delete letters', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  // Delete a single letter
  const handleDeleteSingle = async (letterId: string) => {
    try {
      await deleteLoadLetter(letterId);
      addToast('Letter deleted successfully', 'success');
      await fetchLetters();
    } catch (error) {
      console.error('Error deleting letter:', error);
      addToast('Failed to delete letter', 'error');
    }
  };

  // Handle PDF download
  const handleDownloadPdf = async (letter: LoadLetter) => {
    setIsDownloadingPdf(true);
    try {
      await generateLoadLetterPdf(letter);
      addToast('PDF downloaded successfully', 'success');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      addToast('Failed to download PDF. Please try again.', 'error');
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  // Friendly label for a stored semester key
  const formatSemester = (semester: string) => {
    if (semester === 'semester1') return 'Semester 1';
    if (semester === 'semester2') return 'Semester 2';
    if (semester === 'both') return 'Full Year';
    return semester;
  };

  // Filter letters
  const getFilteredLetters = () => {
    return loadLetters.filter(letter => {
      // Semester filter — the top Semester selector scopes the visible list so each
      // staff member's Sem 1 and Sem 2 letters don't appear together as apparent
      // duplicates. 'both' is the full-year view and shows every letter.
      if (selectedSemester !== 'both' && letter.semester !== selectedSemester) {
        return false;
      }

      // Learning-area filter (normalise both sides so raw and canonical match)
      if (filterDepartment !== 'all' && displayLearningArea(letter.department) !== filterDepartment) {
        return false;
      }

      // Status filter
      if (filterStatus !== 'all' && letter.status !== filterStatus) {
        return false;
      }

      // Search query
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          letter.staffName.toLowerCase().includes(query) ||
          letter.staffCode.toLowerCase().includes(query)
        );
      }

      return true;
    });
  };

  // Get unique learning areas (canonical, deduped)
  const getUniqueDepartments = () => {
    const departments = new Set<string>();
    staff.forEach(s => {
      const area = displayLearningArea(s.department, '');
      if (area) departments.add(area);
    });
    return Array.from(departments).sort();
  };

  // Toggle letter selection
  const toggleLetterSelection = (letterId: string) => {
    setSelectedLetters(prev =>
      prev.includes(letterId)
        ? prev.filter(id => id !== letterId)
        : [...prev, letterId]
    );
  };

  // Toggle all letters
  const toggleAllLetters = () => {
    const filtered = getFilteredLetters();
    if (selectedLetters.length === filtered.length) {
      setSelectedLetters([]);
    } else {
      setSelectedLetters(filtered.map(l => l.id));
    }
  };

  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'draft':
        return (
          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-ink-100 text-ink-900">
            Draft
          </span>
        );
      case 'ready':
        return (
          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-navy-100 text-navy-800">
            Ready
          </span>
        );
      case 'released':
        return (
          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-ok-bg text-ok-fg">
            Released
          </span>
        );
      case 'acknowledged':
        return (
          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-info-bg text-info-fg">
            <CheckCircle className="h-3 w-3 mr-1" />
            Acknowledged
          </span>
        );
      case 'queried':
        return (
          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-warn-bg text-warn-fg">
            <AlertTriangle className="h-3 w-3 mr-1" />
            Queried
          </span>
        );
      default:
        return null;
    }
  };

  // Get utilization color
  const getUtilizationColor = (utilization: number) => {
    if (utilization < 85) return 'text-navy-600';
    if (utilization < 95) return 'text-navy-500';
    if (utilization <= 105) return 'text-ok-fg';
    if (utilization <= 115) return 'text-warn-fg';
    return 'text-danger-fg';
  };

  // Render preview modal
  const renderPreviewModal = () => {
    if (!showPreview || !selectedLetter) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
          <div className="p-6 border-b flex justify-between items-center">
            <h3 className="text-section text-ink-900">
              Preview: {selectedLetter.staffName} - {selectedLetter.semester} {selectedLetter.academicYear}
            </h3>
            <button
              className="text-ink-400 hover:text-ink-500 text-2xl"
              onClick={() => {
                setShowPreview(false);
                setSelectedLetter(null);
              }}
            >
              &times;
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            <div
              className="prose max-w-none print-letter"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedLetter.generatedHtml || '<p>No content available</p>') }}
            />
          </div>

          <div className="p-6 border-t flex justify-between no-print">
            <div className="flex space-x-2">
              <Button
                variant="secondary"
                leftIcon={<Download className="h-4 w-4" />}
                onClick={() => handleDownloadPdf(selectedLetter)}
                loading={isDownloadingPdf}
                disabled={isDownloadingPdf}
              >
                Download PDF
              </Button>
              {selectedLetter.status === 'draft' && (
                <Button
                  variant="secondary"
                  leftIcon={<CheckCircle className="h-4 w-4" />}
                  onClick={async () => {
                    await handleMarkSingleReady(selectedLetter.id);
                    setShowPreview(false);
                    setSelectedLetter(null);
                  }}
                >
                  Mark as Ready
                </Button>
              )}
              {selectedLetter.status === 'ready' && (
                <Button
                  variant="primary"
                  leftIcon={<Send className="h-4 w-4" />}
                  onClick={async () => {
                    await handleReleaseSingle(selectedLetter.id);
                    setShowPreview(false);
                    setSelectedLetter(null);
                  }}
                >
                  Release to Staff
                </Button>
              )}
            </div>
            <Button
              variant="secondary"
              onClick={() => {
                setShowPreview(false);
                setSelectedLetter(null);
              }}
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Render release modal
  const renderReleaseModal = () => {
    if (!showReleaseModal) return null;

    const readyLetters = loadLetters.filter(
      l => selectedLetters.includes(l.id) && l.status === 'ready'
    );
    const draftLetters = loadLetters.filter(
      l => selectedLetters.includes(l.id) && l.status === 'draft'
    );

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
          <div className="p-6">
            <h3 className="text-section text-ink-900 mb-4">Release Load Letters to Staff</h3>

            <div className="bg-navy-50 border border-navy-200 rounded-md p-4 mb-4">
              <p className="text-sm text-navy-700">
                You are about to release <strong>{readyLetters.length}</strong> load letter(s).
                Staff will be able to view and acknowledge their letters.
              </p>
            </div>

            {draftLetters.length > 0 && (
              <div className="bg-ink-50 border border-ink-200 rounded-md p-4 mb-4">
                <p className="text-sm text-ink-700">
                  <strong>{draftLetters.length}</strong> draft letter(s) selected will not be released.
                  Mark them as ready first to include them.
                </p>
              </div>
            )}

            {readyLetters.length === 0 && (
              <div className="bg-warn-bg border border-warn-border rounded-md p-4 mb-6">
                <div className="flex">
                  <AlertTriangle className="h-5 w-5 text-warn-fg mr-2 flex-shrink-0" />
                  <p className="text-sm text-warn-fg">
                    No ready letters selected. Please mark letters as ready before releasing.
                  </p>
                </div>
              </div>
            )}

            {readyLetters.length > 0 && (
              <div className="bg-warn-bg border border-warn-border rounded-md p-4 mb-6">
                <div className="flex">
                  <AlertTriangle className="h-5 w-5 text-warn-fg mr-2 flex-shrink-0" />
                  <p className="text-sm text-warn-fg">
                    Once released, letters cannot be easily recalled. Make sure all information is correct.
                  </p>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <Button
                variant="secondary"
                onClick={() => setShowReleaseModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                leftIcon={<Send className="h-4 w-4" />}
                onClick={handleReleaseLetters}
                loading={isReleasing}
                disabled={readyLetters.length === 0}
              >
                Release {readyLetters.length} Letter{readyLetters.length !== 1 ? 's' : ''}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render delete modal
  const renderDeleteModal = () => {
    if (!showDeleteModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
          <div className="p-6">
            <h3 className="text-section text-ink-900 mb-4">Delete Load Letters</h3>

            <div className="bg-danger-bg border border-danger-border rounded-md p-4 mb-4">
              <div className="flex">
                <AlertTriangle className="h-5 w-5 text-danger-fg mr-2 flex-shrink-0" />
                <p className="text-sm text-danger-fg">
                  You are about to delete <strong>{selectedLetters.length}</strong> load letter(s).
                  This action cannot be undone.
                </p>
              </div>
            </div>

            <div className="bg-ink-50 border border-ink-200 rounded-md p-4 mb-6">
              <p className="text-sm text-ink-700">
                Deleted letters will be permanently removed from the system.
                Staff will no longer have access to these letters.
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <Button
                variant="secondary"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                leftIcon={<Trash2 className="h-4 w-4" />}
                onClick={handleDeleteLetters}
                loading={isDeleting}
              >
                Delete {selectedLetters.length} Letter{selectedLetters.length !== 1 ? 's' : ''}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div>
        <PageHeader
          title="Load Letter Generation"
          description="Generate and manage teaching load letters"
        />
        <Card>
          <div className="flex justify-center py-12">
            <Spinner size="lg" label="Loading…" />
          </div>
        </Card>
      </div>
    );
  }

  const filteredLetters = getFilteredLetters();

  return (
    <div>
      <PageHeader
        title="Load Letter Generation"
        description={`Generate and manage teaching load letters for ${settings?.currentAcademicYear || ''}`}
        actions={
          <div className="flex space-x-2">
            <Button
              variant="secondary"
              leftIcon={<RefreshCw className="h-4 w-4" />}
              onClick={fetchLetters}
            >
              Refresh
            </Button>
            <Button
              variant="primary"
              leftIcon={<FileText className="h-4 w-4" />}
              onClick={() => handleGenerateLetters()}
              loading={isGenerating}
              disabled={isGenerating}
            >
              {isGenerating ? 'Generating...' : 'Generate All Letters'}
            </Button>
            {selectedLetters.length > 0 && (
              <Button
                variant="secondary"
                leftIcon={<Users className="h-4 w-4" />}
                onClick={() => {
                  const selectedStaffIds = loadLetters
                    .filter(l => selectedLetters.includes(l.id))
                    .map(l => l.staffId);
                  handleGenerateLetters(selectedStaffIds);
                }}
                loading={isGenerating}
                disabled={isGenerating}
              >
                Regenerate for Selected
              </Button>
            )}
          </div>
        }
      />

      {/* Generation Progress */}
      {isGenerating && generationProgress.total > 0 && (
        <Card className="mb-6">
          <div className="text-center">
            <p className="text-sm text-ink-500 mb-2">
              Generating letters: {generationProgress.current} / {generationProgress.total}
            </p>
            <div className="w-full bg-ink-200 rounded-full h-2">
              <div
                className="bg-navy-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(generationProgress.current / generationProgress.total) * 100}%` }}
              ></div>
            </div>
          </div>
        </Card>
      )}

      {/* Settings Bar */}
      <Card className="mb-6">
        <div className="flex flex-wrap items-center gap-4">
          {/* Year Selector */}
          <div className="flex-shrink-0">
            <label className="block text-sm font-medium text-ink-700 mb-1">Academic Year</label>
            <YearSelector showGridCount={true} />
          </div>

          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-ink-700 mb-1">Grid ({workingYear})</label>
            <select
              className="block w-full border border-ink-300 rounded-md py-2 px-3 text-sm"
              value={selectedGridId}
              onChange={e => {
                const value = e.target.value;
                setSelectedGridId(value);
                if (value !== 'all') {
                  selectGrid(value);
                }
              }}
              disabled={gridLoading}
            >
              {gridLoading ? (
                <option>Loading grids...</option>
              ) : grids.length === 0 ? (
                <option>No grids available</option>
              ) : (
                <>
                  <option value="all">All Grids (Combined)</option>
                  {grids.map(grid => (
                    <option key={grid.id} value={grid.id}>
                      {grid.name} {grid.year ? `(${grid.year})` : ''}
                    </option>
                  ))}
                </>
              )}
            </select>
            {selectedGridId === 'all' ? (
              <p className="mt-1 text-xs text-ink-500">
                Combining data from {grids.length} grid{grids.length !== 1 ? 's' : ''}
              </p>
            ) : selectedGrid ? (
              <p className="mt-1 text-xs text-ink-500">
                {getStats()?.totalClasses || 0} classes, {getStats()?.totalStaff || 0} staff
              </p>
            ) : null}
          </div>

          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-ink-700 mb-1">Semester</label>
            <select
              className="block w-full border border-ink-300 rounded-md py-2 px-3 text-sm"
              value={selectedSemester}
              onChange={e => {
                // Switching the visible semester clears any row selection so letters
                // from the now-hidden semester can't be swept into a bulk action.
                semesterPicked.current = true;
                setSelectedSemester(e.target.value as Semester);
                setSelectedLetters([]);
              }}
            >
              <option value="semester1">Semester 1</option>
              <option value="semester2">Semester 2</option>
              <option value="both">Full Year</option>
            </select>
          </div>

          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-ink-700 mb-1">Template</label>
            <select
              className="block w-full border border-ink-300 rounded-md py-2 px-3 text-sm"
              value={selectedTemplate || ''}
              onChange={e => setSelectedTemplate(e.target.value)}
            >
              {templates.map(template => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {gridError && (
          <div className="mt-4 p-3 bg-danger-bg border border-danger-border rounded-md">
            <p className="text-sm text-danger-fg">Grid Error: {gridError}</p>
          </div>
        )}

        {/* Year mismatch warning */}
        {settings && Number(settings.currentAcademicYear) !== workingYear && (
          <div className="mt-4 p-3 bg-warn-bg border border-warn-border rounded-md flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-warn-fg flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-warn-fg">
                Year Mismatch Detected
              </p>
              <p className="text-sm text-warn-fg mt-1">
                Load letter settings are configured for {settings.currentAcademicYear}, but you're viewing {workingYear} grids.
                Letters will be generated for {workingYear}. Update Load Letter Settings to match if this is unintentional.
              </p>
            </div>
          </div>
        )}

        {!gridLoading && grids.length === 0 && (
          <div className="mt-4 p-3 bg-warn-bg border border-warn-border rounded-md">
            <p className="text-sm text-warn-fg">
              No grids available for {workingYear}. Please create a grid first from the Grid Editor or select a different year.
            </p>
          </div>
        )}
      </Card>

      {/* Filters and Actions */}
      <Card className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 mb-4">
          <div className="flex-1 flex flex-col md:flex-row md:items-center space-y-4 md:space-y-0 md:space-x-4">
            <div className="w-full md:w-64">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-5 w-5 text-ink-400" />
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-ink-300 rounded-md text-sm"
                  placeholder="Search staff..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            <select
              className="border border-ink-300 rounded-md py-2 px-3 text-sm"
              value={filterDepartment}
              onChange={e => setFilterDepartment(e.target.value)}
            >
              <option value="all">All Learning Areas</option>
              {getUniqueDepartments().map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>

            <select
              className="border border-ink-300 rounded-md py-2 px-3 text-sm"
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="ready">Ready</option>
              <option value="released">Released</option>
              <option value="acknowledged">Acknowledged</option>
              <option value="queried">Queried</option>
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <span className="text-sm text-ink-500">
              {filteredLetters.length} of {loadLetters.length} letters
            </span>
          </div>
        </div>

        {selectedLetters.length > 0 && (
          <div className="flex items-center justify-between p-3 bg-navy-50 border border-navy-200 rounded-md">
            <div className="flex flex-col">
              <span className="text-sm font-medium text-navy-700">
                {selectedLetters.length} letter(s) selected
              </span>
              <span className="text-xs text-navy-600">
                {loadLetters.filter(l => selectedLetters.includes(l.id) && l.status === 'draft').length} draft,{' '}
                {loadLetters.filter(l => selectedLetters.includes(l.id) && l.status === 'ready').length} ready,{' '}
                {loadLetters.filter(l => selectedLetters.includes(l.id) && l.status === 'released').length} released
              </span>
            </div>
            <div className="flex space-x-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setSelectedLetters([])}
              >
                Clear Selection
              </Button>
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<CheckCircle className="h-4 w-4" />}
                onClick={handleMarkAsReady}
                disabled={!loadLetters.some(l => selectedLetters.includes(l.id) && l.status === 'draft')}
              >
                Mark as Ready
              </Button>
              <Button
                variant="primary"
                size="sm"
                leftIcon={<Send className="h-4 w-4" />}
                onClick={() => setShowReleaseModal(true)}
                disabled={!loadLetters.some(l => selectedLetters.includes(l.id) && l.status === 'ready')}
              >
                Release Selected
              </Button>
              <Button
                variant="danger"
                size="sm"
                leftIcon={<Trash2 className="h-4 w-4" />}
                onClick={() => setShowDeleteModal(true)}
              >
                Delete Selected
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Letters Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-ink-200">
            <thead className="bg-ink-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-navy-600 border-ink-300 rounded"
                    checked={selectedLetters.length === filteredLetters.length && filteredLetters.length > 0}
                    onChange={toggleAllLetters}
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase">Staff</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase">Learning Area</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase">Semester</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase">FTE</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase">Utilization</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-ink-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-ink-200">
              {filteredLetters.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-4">
                    <EmptyState
                      icon={<FileText className="h-6 w-6" />}
                      title="No load letters found"
                      description={<>Click "Generate Letters" to create load letters for staff.</>}
                    />
                  </td>
                </tr>
              ) : (
                filteredLetters.map(letter => (
                  <tr key={letter.id} className="hover:bg-ink-50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-navy-600 border-ink-300 rounded"
                        checked={selectedLetters.includes(letter.id)}
                        onChange={() => toggleLetterSelection(letter.id)}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-ink-900">{letter.staffName}</div>
                      <div className="text-xs text-ink-500 font-mono tabular-nums">{letter.staffCode}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-500">
                      {displayLearningArea(letter.department)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-500">
                      {formatSemester(letter.semester)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-500 font-mono tabular-nums">
                      {letter.fte.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm font-medium font-mono tabular-nums ${getUtilizationColor(letter.utilization)}`}>
                        {letter.utilization}%
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(letter.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          leftIcon={<Eye className="h-4 w-4" />}
                          onClick={() => {
                            setSelectedLetter(letter);
                            setShowPreview(true);
                          }}
                        >
                          Preview
                        </Button>
                        {letter.status === 'draft' && (
                          <Button
                            variant="secondary"
                            size="sm"
                            leftIcon={<CheckCircle className="h-4 w-4" />}
                            onClick={() => handleMarkSingleReady(letter.id)}
                          >
                            Mark Ready
                          </Button>
                        )}
                        {letter.status === 'ready' && (
                          <Button
                            variant="primary"
                            size="sm"
                            leftIcon={<Send className="h-4 w-4" />}
                            onClick={() => handleReleaseSingle(letter.id)}
                          >
                            Release
                          </Button>
                        )}
                        <Button
                          variant="danger"
                          size="sm"
                          leftIcon={<Trash2 className="h-4 w-4" />}
                          onClick={async () => {
                            if (await confirm({
                              title: 'Delete letter?',
                              message: `Delete letter for ${letter.staffName}?`,
                              confirmLabel: 'Delete',
                              variant: 'danger',
                            })) {
                              handleDeleteSingle(letter.id);
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Modals */}
      {renderPreviewModal()}
      {renderReleaseModal()}
      {renderDeleteModal()}
    </div>
  );
};

export default LetterGeneration;
